import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
      {/* Hero Section */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-[#F1F5F8]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[#163A59] font-medium mb-6 hover:gap-3 transition-all"
            >
              <ArrowLeft size={18} />
              Back to Home
            </Link>
            <h1 className="font-['Source_Serif_4'] text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-slate-600">Last updated: February 2026</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="prose prose-slate max-w-none">
            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">1. Introduction</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              The Geneva Institute of Humanitarian & Development Studies (GIHDS) is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website www.gihds.org and use our academic and professional services, including the Master of Humanitarian Action & Development, the Master of Development Project & Program Management, the Doctor of Humanitarian Action & Development, and the AI in Humanitarian Action & Development short course.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">2. Information We Collect</h2>
            <p className="text-slate-600 leading-relaxed mb-4">We may collect the following categories of information:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li><strong>Application information:</strong> full name, email, phone number, nationality, country of residence, date of birth, and gender</li>
              <li><strong>Academic and professional information:</strong> highest qualification, institution, field of study, graduation year, current occupation, organization, and years of experience</li>
              <li><strong>Motivation and career goals:</strong> the personal statement and career-goal responses you submit through the application form</li>
              <li><strong>Contact-form information:</strong> name, email, subject, and message content</li>
              <li><strong>Technical information:</strong> IP address, browser type, device information, referring pages, and general usage statistics collected automatically when you visit the site</li>
            </ul>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-600 leading-relaxed mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>Process and evaluate your application for admission</li>
              <li>Assess eligibility for the GIHDS 90% scholarship (available to eligible applicants from Africa and Asia)</li>
              <li>Communicate with you about your application, program updates, and academic matters</li>
              <li>Deliver educational services, including LMS access and faculty support</li>
              <li>Respond to inquiries submitted through our contact form</li>
              <li>Improve our website, curricula, and services</li>
              <li>Comply with legal, regulatory, and academic reporting obligations</li>
            </ul>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">4. Legal Basis for Processing</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              We process your personal information based on one or more of the following legal grounds: your consent (submitted through our forms), the performance of an educational agreement between you and GIHDS, our legitimate interest in operating an academic institution, and compliance with legal obligations that apply to us.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">5. Data Protection & Security</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              We implement appropriate technical and organizational safeguards to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These include encrypted transmission (HTTPS), access controls on our databases, restricted staff access on a need-to-know basis, rate limiting and input validation on public endpoints, and secure email delivery for administrative notifications. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">6. Data Sharing</h2>
            <p className="text-slate-600 leading-relaxed mb-4">We do <strong>not</strong> sell your personal information. We may share information only in the following limited circumstances:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>With trusted service providers who assist us in operating the website, hosting infrastructure, learning-management system, or sending transactional emails, under confidentiality obligations and strictly to perform their services on our behalf</li>
              <li>With academic partners strictly for the purposes of program delivery, verification of academic documents, or research collaboration</li>
              <li>Where required by law, regulation, court order, or governmental authority</li>
              <li>Where necessary to protect the rights, property, or safety of GIHDS, our learners, staff, or the public</li>
            </ul>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">7. Data Retention</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              We retain application data and academic records for as long as necessary to fulfil the purposes described in this Policy, including the issuance and future verification of academic documents (Master's Degree Certificate, Doctoral Degree Certificate, Academic Transcript, and Certificate of Completion), and to comply with applicable legal, regulatory, and accreditation obligations. Contact-form messages are retained for a reasonable operational period and then archived or deleted.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">8. Your Rights</h2>
            <p className="text-slate-600 leading-relaxed mb-4">Depending on your location, you may have the right to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate or outdated data</li>
              <li>Request deletion of your data (subject to academic-record retention obligations)</li>
              <li>Object to or restrict certain processing of your data</li>
              <li>Request portability of your data in a common electronic format</li>
              <li>Withdraw consent at any time where processing is based on consent</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6">
              To exercise any of these rights, please contact us using the details in Section 11.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">9. Cookies & Website Analytics</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              Our website may use essential cookies to enable core functionality (such as navigation, language preference, and form protection). We do not use tracking cookies for advertising. If we introduce optional analytics cookies in the future, they will be enabled only with your consent.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">10. Changes to This Policy</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              We may update this Privacy Policy periodically to reflect changes in our practices, technologies, legal requirements, or services. Material changes will be posted on this page with a revised "Last updated" date.
            </p>

            <h2 className="font-['Source_Serif_4'] text-2xl font-semibold text-slate-900 mb-4">11. Contact</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              If you have questions about this Privacy Policy or wish to exercise your rights, please contact us:
            </p>
            <div className="bg-[#F1F5F8] p-6 rounded-sm">
              <p className="text-slate-900 font-semibold">Geneva Institute of Humanitarian & Development Studies (GIHDS)</p>
              <p className="text-slate-600">Email: admissions@gihds.org</p>
              <p className="text-slate-600">Website: www.gihds.org</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
